// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api.js';
import Logo from '../components/shared/Logo.jsx';
import { AlertTriangle, CheckCircle, Check, X } from 'lucide-react';

export default function Export() {
  const { catalogId } = useParams();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [products, setProducts] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);

  useEffect(() => {
    loadData();
  }, [catalogId]);

  async function loadData() {
    try {
      const [catData, prodData] = await Promise.all([
        api.getCatalog(catalogId),
        api.getProducts(catalogId)
      ]);
      setCatalog(catData.catalog);
      setProducts(prodData.products || []);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleExport() {
    setExporting(true);
    setError('');
    try {
      const data = await api.exportCatalog(catalogId);
      setResult(data);
    } catch (err) {
      setError(err.message);
      if (err.message.includes('paid plan')) {
        setError('Export requires a paid plan. Upgrade to Starter, Growth, or Pro to export.');
      }
    } finally {
      setExporting(false);
    }
  }

  async function handleFeedback(success) {
    try {
      await api.submitFeedback({ catalog_id: catalogId, import_success: success, feedback_text: feedback });
      setFeedbackSent(true);
    } catch {}
  }

  const allVariants = products.flatMap(p => p.variants || []);
  const errorCount = allVariants.flatMap(v => v.flags || []).filter(f => f.severity === 'error' && !f.resolved).length;
  const warningCount = allVariants.flatMap(v => v.flags || []).filter(f => f.severity === 'warning' && !f.resolved).length;
  const greenPct = allVariants.length > 0 ? Math.round((allVariants.filter(v => v.row_status === 'green').length / allVariants.length) * 100) : 0;

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <Logo iconSize={24} onClick={() => navigate('/dashboard')} />
          <button onClick={() => navigate(`/review/${catalogId}`)} style={styles.backBtn}>← Back to Review</button>
        </div>
      </header>

      <main style={styles.main}>
        <h1 style={styles.title}>Export to Shopify</h1>
        <p style={styles.subtitle}>{catalog?.name}</p>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.summary}>
          <h3 style={styles.sectionTitle}>Confidence Summary</h3>
          <div style={styles.summaryGrid}>
            <div style={styles.summaryItem}>
              <span style={{ ...styles.summaryNum, color: 'var(--color-green-text)' }}>{greenPct}%</span>
              <span style={styles.summaryLabel}>Ready to export</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={{ ...styles.summaryNum, color: errorCount > 0 ? 'var(--color-red-text)' : 'var(--color-text-2)' }}>{errorCount}</span>
              <span style={styles.summaryLabel}>Errors remaining</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={{ ...styles.summaryNum, color: warningCount > 0 ? 'var(--color-yellow-text)' : 'var(--color-text-2)' }}>{warningCount}</span>
              <span style={styles.summaryLabel}>Warnings</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryNum}>{products.length}</span>
              <span style={styles.summaryLabel}>Products</span>
            </div>
          </div>

          {errorCount > 0 && (
            <div style={styles.warningNote}>
              <AlertTriangle size={16} style={{ color: 'var(--color-yellow)', flexShrink: 0 }} /> {errorCount} error{errorCount > 1 ? 's' : ''} detected. We recommend fixing these in the review table before exporting for best results.
            </div>
          )}
        </div>

        {result ? (
          <div style={styles.successCard}>
            <div style={styles.successIcon}><CheckCircle size={32} style={{ color: 'var(--color-green)' }} /></div>
            <h2 style={styles.successTitle}>Export Ready!</h2>
            <p style={styles.successDesc}>{result.row_count} rows generated</p>
            {result.csv_url && (
              <a href={result.csv_url} download style={styles.downloadBtn}>
                Download Shopify CSV
              </a>
            )}

            {!feedbackSent ? (
              <div style={styles.feedbackSection}>
                <p style={styles.feedbackTitle}>Did your Shopify import succeed?</p>
                <div style={styles.feedbackBtns}>
                  <button onClick={() => handleFeedback(true)} style={styles.feedbackBtn}>Yes <Check size={14} /></button>
                  <button onClick={() => handleFeedback(false)} style={{ ...styles.feedbackBtn, background: 'var(--color-red-bg)', color: 'var(--color-red-text)' }}>No <X size={14} /></button>
                </div>
                <textarea
                  value={feedback}
                  onChange={e => setFeedback(e.target.value)}
                  placeholder="Optional: any comments about the export quality?"
                  style={styles.feedbackText}
                />
              </div>
            ) : (
              <p style={styles.feedbackThanks}><Check size={14} style={{ color: 'var(--color-green)', display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />Thanks for your feedback!</p>
            )}
          </div>
        ) : (
          <button onClick={handleExport} disabled={exporting} style={styles.exportBtn}>
            {exporting ? 'Generating CSV...' : 'Generate Shopify CSV'}
          </button>
        )}
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--color-bg)' },
  header: { background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)' },
  headerInner: { maxWidth: '800px', margin: '0 auto', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  backBtn: { background: 'none', border: 'none', color: 'var(--color-text-2)', cursor: 'pointer', fontSize: '14px' },
  main: { maxWidth: '700px', margin: '0 auto', padding: '40px 24px' },
  title: { fontSize: '28px', fontWeight: '800', marginBottom: '4px', color: 'var(--color-text)' },
  subtitle: { color: 'var(--color-text-2)', marginBottom: '32px' },
  error: { background: 'var(--color-red-bg)', border: '1px solid var(--color-red-border)', color: 'var(--color-red-text)', borderRadius: '8px', padding: '12px 16px', marginBottom: '24px', fontSize: '14px' },
  summary: { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '12px', padding: '24px', marginBottom: '24px' },
  sectionTitle: { fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: 'var(--color-text)' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '16px' },
  summaryItem: { textAlign: 'center' },
  summaryNum: { display: 'block', fontSize: '28px', fontWeight: '800', color: 'var(--color-text)' },
  summaryLabel: { display: 'block', fontSize: '12px', color: 'var(--color-text-2)', marginTop: '2px' },
  warningNote: { background: 'var(--color-yellow-bg)', border: '1px solid var(--color-yellow-border)', color: 'var(--color-yellow-text)', borderRadius: '8px', padding: '10px 14px', fontSize: '13px' },
  exportBtn: { width: '100%', background: 'var(--color-green)', color: '#fff', border: 'none', borderRadius: '10px', padding: '16px', fontSize: '17px', fontWeight: '700', cursor: 'pointer' },
  successCard: { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '12px', padding: '40px', textAlign: 'center' },
  successIcon: { fontSize: '48px', marginBottom: '12px' },
  successTitle: { fontSize: '24px', fontWeight: '800', marginBottom: '4px', color: 'var(--color-text)' },
  successDesc: { color: 'var(--color-text-2)', marginBottom: '24px' },
  downloadBtn: { display: 'inline-block', background: 'var(--color-green)', color: '#fff', borderRadius: '10px', padding: '14px 32px', fontSize: '16px', fontWeight: '700', marginBottom: '32px', textDecoration: 'none' },
  feedbackSection: { borderTop: '1px solid var(--color-border)', paddingTop: '24px' },
  feedbackTitle: { fontWeight: '600', marginBottom: '12px', color: 'var(--color-text)' },
  feedbackBtns: { display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '12px' },
  feedbackBtn: { background: 'var(--color-green-bg)', color: 'var(--color-green-text)', border: '1px solid var(--color-green-border)', borderRadius: '8px', padding: '8px 24px', cursor: 'pointer', fontWeight: '600', fontSize: '15px' },
  feedbackText: { width: '100%', padding: '10px', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '14px', minHeight: '80px', resize: 'vertical', background: 'var(--color-surface)', color: 'var(--color-text)' },
  feedbackThanks: { color: 'var(--color-text-2)', fontStyle: 'italic', paddingTop: '16px', borderTop: '1px solid var(--color-border)' }
};
